// Hideo - "Drop your idea here" on the Home (src/components/IdeaDrop.vue, state in src/stores/idea.ts): an optional
// title, the idea written out, an image and a voice note (any of the three is enough) become a new video in the plan, in
// the "Idea" column. The title (or, without one, the start of the text) becomes the video's title, the text its notes
// ("Appunti"), the image the thumbnail, the voice note the video's voiceNote. Image and voice note are stored apart
// (src/features/cloud/media.ts): the video keeps the "media:<id>" reference.
import { t } from '../i18n'
import type { ProfileData, Video } from '../data/types'
import { newVideo } from '../data/newVideo'

export const IDEA_MAX = 30
/** Longest voice note: the recording stops by itself (seconds) */
export const VOICE_MAX_SEC = 180
/** Largest audio file that can be chosen or dropped (bytes) */
export const IDEA_AUDIO_MAX = 3e6
/** Voice quality, so saves stay light */
export const VOICE_BITRATE = 32000

/** What the box holds: the optional title, the written idea (with @mentions), the image and the recorded audio (data URLs) */
export interface IdeaDraft {
  title: string
  text: string
  image: string
  audio: string
  /** A recording is in progress */
  recording: boolean
}
/** The idea can be added: some text, an image or a finished voice note (never during a recording) */
export const ideaReady = (s: IdeaDraft): boolean => !s.recording && (!!s.text.trim() || !!s.image || !!s.audio)
/** Title of the new video: the optional title, otherwise the first line of the text; cut to 30 characters (at a word
 *  when possible); "Untitled" when both are empty. The default title is created in the UI language: once stored it is
 *  user content. Mentions ("@[type:id]") are left out of a title made from the text. */
export function ideaTitle(s: Pick<IdeaDraft, 'title' | 'text'>): string {
  const own = s.title.trim()
  if (own) return own.slice(0, IDEA_MAX)
  const line = (s.text.trim().split('\n')[0] || '').replace(/@\[[a-z]+:[^\]]*\]/gi, '').replace(/\s+/g, ' ').trim()
  if (line.length <= IDEA_MAX) return line || t('Untitled')
  const cut = line.slice(0, IDEA_MAX), sp = cut.lastIndexOf(' ')
  return (sp >= IDEA_MAX / 2 ? cut.slice(0, sp) : cut).trim()
}
/** Notes of the new video: the written idea, unless it is the title itself */
export const ideaNotes = (s: Pick<IdeaDraft, 'title' | 'text'>): string => {
  const text = s.text.trim()
  return text === ideaTitle(s) ? '' : text
}
/** Kind of a chosen or dropped file */
export const ideaFileKind = (f: { type: string }): 'image' | 'audio' | null => (f.type.startsWith('image/') ? 'image' : f.type.startsWith('audio/') ? 'audio' : null)
/** Audio formats to record in, in order of preference (the first one the browser supports) */
export const REC_TYPES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm']

/** The new video of the plan ("Idea" column); image, voiceNote = the stored references ('' when missing) */
export function ideaVideo(d: ProfileData, o: { id: string; title: string; notes: string; projectId: string; image: string; voiceNote: string }): Video {
  return newVideo(d, { id: o.id, title: o.title, projectId: o.projectId, thumbnail: o.image, voiceNote: o.voiceNote }, { notes: o.notes })
}
